import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { endpoints } from "@/core/api/endpoints";
import {
  invalidateAfterMutation,
  walletTransactionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import type {
  PayrollInitiateRequestDto,
  PayrollRunReport,
  PayrollTransactionDto,
} from "@/features/payroll/api/dto";

/**
 * Lance un lot de paie : un `POST /Transactions/initiate` (wallet-service,
 * §5.3) PAR bénéficiaire, type `PAYROLL_CREDIT` (annexe A). Appels
 * séquentiels pour un compte rendu ligne à ligne fiable.
 *
 * Idempotency-Key OBLIGATOIRE (§4.7) : chaque versement est une tentative
 * métier distincte (corps différent) → une clé FRAÎCHE par bénéficiaire.
 * Un échec sur une ligne n'interrompt pas le lot : le rapport
 * (`succeeded`/`failed`) est affiché à l'opérateur, qui relance les seules
 * lignes en échec après correction.
 *
 * Réplique l'appel du hook `useInitiateTransaction` de wallet via le
 * registre `@/core/api/endpoints` (règle : pas d'import croisé entre
 * features) — le hook wallet ne couvre que l'initiation unitaire.
 */
export const useInitiatePayrollRun = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["payroll", "runs", "initiate"],
    mutationFn: async (
      payloads: PayrollInitiateRequestDto[]
    ): Promise<PayrollRunReport> => {
      const succeeded: PayrollTransactionDto[] = [];
      const failed: PayrollRunReport["failed"] = [];
      for (const [index, reqBody] of payloads.entries()) {
        try {
          const transaction = await mutateIdempotent<PayrollTransactionDto>({
            scopeId: `payroll.runs.initiate.${index}`,
            url: endpoints.wallet.transactions.initiate,
            body: reqBody,
          });
          succeeded.push(transaction);
        } catch (error) {
          const message =
            getBackendErrorMessage(error) ?? apiErrorHandler(error);
          failed.push({ index, message });
          logger.error(
            `useInitiatePayrollRun line ${index + 1} failed: ${message}`,
            error instanceof Error ? error : undefined,
            { prefix: "payroll" }
          );
        }
      }
      return { succeeded, failed };
    },
    onSuccess: () => {
      return invalidateAfterMutation(
        queryClient,
        walletTransactionCacheKeys(),
        { resetListPage: true }
      );
    },
  });
};
